import type {
  TdUnit,
  TdVehicleModel,
  TdVehicleVersion,
  TestDriveDynamicStructure,
} from "./types"

// Converte a `dynamicStructure` (mesma resolvida no builder e no renderer) em
// modelos + versões (com galeria 360) + unidades. Portado do `mapEntries` do
// SimuladorFinanciamento — resolução de campos por TIPO via `_metadata.fields`
// (independe do nome dado ao campo), com convenção de nomes como fallback.

type Loose = Record<string, unknown>

interface FieldMeta {
  id?: string
  name?: string
  type?: string
}

interface DynamicRelationBucket {
  contentTypeName?: string
  contentTypeSlug?: string
  items?: Loose[]
}

const metaFields = (entry: Loose): FieldMeta[] =>
  (entry._metadata as { fields?: FieldMeta[] } | undefined)?.fields ?? []

/** Valor do primeiro campo do `type` informado, descoberto via `_metadata`. */
const byType = (entry: Loose, type: string): unknown => {
  const name = metaFields(entry).find((f) => f.type === type)?.name
  return name ? entry[name] : undefined
}

const norm = (s: string): string => s.toLowerCase()

const IMAGE_AVOID = /(banner|logo|icone|icon|fundo|background|selo|bandeira|marca)/
const IMAGE_PREFER = /(foto|imagem|image|photo|capa|cover|principal|modelo|veic|carro)/
const IMAGE_MAIN = /(principal|profile)/
const IMAGE_ALT = /(alternativ|secundari)/

const imageScore = (name: string): number => {
  if (IMAGE_AVOID.test(name)) return 0
  let score = IMAGE_PREFER.test(name) ? 2 : 1
  if (IMAGE_MAIN.test(name)) score = 4
  if (IMAGE_ALT.test(name)) score -= 1
  return score
}

const first = <T,>(...vals: T[]): T | undefined =>
  vals.find((v) => v != null && v !== "")

const toStr = (v: unknown): string => {
  if (v == null) return ""
  if (typeof v === "string") return v
  if (typeof v === "number" || typeof v === "boolean") return String(v)
  return ""
}

/** Coage um valor de switch/booleano (pode vir como boolean, "true"/"1", etc.). */
const toBool = (v: unknown): boolean => {
  if (typeof v === "boolean") return v
  if (typeof v === "number") return v !== 0
  if (typeof v === "string") return /^(true|1|sim|yes)$/i.test(v.trim())
  return false
}

/** Extrai a URL de um campo de imagem (string | {src} | {url} | {props:{src}} | {desktop} | array). */
const pickImage = (v: unknown): string => {
  if (!v) return ""
  if (typeof v === "string") return v
  if (Array.isArray(v)) return pickImage(v[0])
  if (typeof v === "object") {
    const o = v as Loose
    const desktop = o.desktop as Loose | undefined
    if (desktop) {
      const dp = desktop.props as Loose | undefined
      if (typeof dp?.src === "string") return dp.src
      if (typeof desktop.src === "string") return desktop.src
    }
    const props = o.props as Loose | undefined
    if (typeof props?.src === "string") return props.src
    if (typeof o.src === "string") return o.src
    if (typeof o.url === "string") return o.url
    if (o.cover) return pickImage(o.cover)
    if (o.gallery) return pickImage(o.gallery)
  }
  return ""
}

/** Normaliza um campo de galeria/360 em string[] de URLs. */
const pickGallery = (v: unknown): string[] => {
  if (!v) return []
  if (Array.isArray(v)) return v.map(pickImage).filter(Boolean)
  if (typeof v === "object") {
    const o = v as Loose
    if (Array.isArray(o.gallery)) return o.gallery.map(pickImage).filter(Boolean)
    const single = pickImage(o)
    return single ? [single] : []
  }
  if (typeof v === "string") {
    const t = v.trim()
    if (t.startsWith("[")) {
      try {
        const arr = JSON.parse(t)
        if (Array.isArray(arr)) return arr.map(pickImage).filter(Boolean)
      } catch {
        /* ignore */
      }
    }
    return t
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

/** Melhor imagem via heurística de nome + score (Imagem Principal > foto > alternativa). */
const bestImage = (entry: Loose, fallback: unknown): string => {
  const candidates = metaFields(entry)
    .filter((f) => f.type === "image" && f.name)
    .map((f) => ({
      name: norm(f.name as string),
      url: pickImage(entry[f.name as string]),
    }))
    .filter((c) => c.url)

  if (candidates.length) {
    const ranked = [...candidates].sort(
      (a, b) => imageScore(b.name) - imageScore(a.name) || a.name.localeCompare(b.name),
    )
    return ranked[0].url
  }
  return pickImage(fallback)
}

const GALLERY_NAME = /(360|giro|gallery|galeria|imagens|fotos|frames)/

/** Galeria 360: por tipo (gallery/media), por nome (360/galeria + array) ou array_string. */
const bestGallery = (entry: Loose, fallback: unknown): string[] => {
  const fields = metaFields(entry)
  const candidate =
    fields.find((f) => (f.type === "gallery" || f.type === "media") && f.name) ??
    fields.find(
      (f) => f.name && GALLERY_NAME.test(norm(f.name)) && Array.isArray(entry[f.name]),
    ) ??
    fields.find((f) => f.type === "array_string" && f.name && Array.isArray(entry[f.name]))

  if (candidate?.name) {
    const frames = pickGallery(entry[candidate.name])
    if (frames.length) return frames
  }
  return pickGallery(fallback)
}

const idFrom = (e: Loose, idx: number, prefix: string): string =>
  toStr(first(e.slug, e.id, e._id)) || `${prefix}-${idx}`

const uuidOf = (e: Loose): string | undefined =>
  toStr((e._metadata as { id?: string } | undefined)?.id) || undefined

const relationItems = (entry: Loose): Loose[] => {
  const relations = entry.relations as Record<string, DynamicRelationBucket> | undefined
  if (!relations || typeof relations !== "object") return []
  return Object.values(relations).flatMap((bucket) =>
    Array.isArray(bucket?.items) ? bucket.items : [],
  )
}

const mapVersion = (e: Loose, idx: number): TdVehicleVersion => ({
  id: idFrom(e, idx, "version"),
  uuid: uuidOf(e),
  name: toStr(first(e.nome, e.name, e.versao, e.titulo) ?? byType(e, "text")) || "Versão",
  image:
    bestImage(e, first(e.imagem, e.image, e.foto, e.profile_image, e.thumbnail)) ||
    undefined,
  frames360: bestGallery(
    e,
    first(e.imagens360, e.frames360, e.galeria360, e.rotation_gallery, e["360"]),
  ),
})

const mapModel = (e: Loose, idx: number): TdVehicleModel => ({
  id: idFrom(e, idx, "model"),
  uuid: uuidOf(e),
  name: toStr(first(e.nome, e.name, e.modelo, e.titulo) ?? byType(e, "text")) || "Modelo",
  brand: toStr(first(e.marca, e.brand)) || undefined,
  image: bestImage(e, first(e.imagem, e.image, e.foto, e.capa)),
  frames360: bestGallery(e, first(e.imagens360, e.frames360, e.galeria360, e["360"])),
  versions: relationItems(e).map(mapVersion),
  // Campo `zero-km` (switch) da coleção Seminovos; ausente/false nos modelos.
  zeroKm: toBool(first(e["zero-km"], e["zero_km"], e.zeroKm, e.zerokm)),
})

export const mapVehicles = (section: unknown): TdVehicleModel[] => {
  if (!Array.isArray(section)) return []
  return section
    .filter((e): e is Loose => !!e && typeof e === "object")
    .map(mapModel)
}

export const mapUnits = (units: unknown): TdUnit[] => {
  if (!Array.isArray(units)) return []
  return units
    .filter((u): u is Loose => !!u && typeof u === "object")
    .map((u, idx) => ({
      id: toStr(first(u.id, u._id)) || `unit-${idx}`,
      name: toStr(first(u.displayName, u.name)) || "Unidade",
      address: toStr(u.address) || undefined,
      city: toStr(u.city) || undefined,
      state: toStr(u.state) || undefined,
      mapLink: toStr(first(u.mapLink, u.mapEmbed)) || undefined,
    }))
}

export const mapEntries = (
  structure?: TestDriveDynamicStructure | null,
): { vehicles: TdVehicleModel[]; units: TdUnit[] } => ({
  vehicles: mapVehicles(structure?.section),
  units: mapUnits(structure?.channel?.units),
})
